import type { LiveChapter } from '../types/liveCall';
import type { PanelSource, PanelState } from './roomMatchSync';

/**
 * The match as session state (decision 130): whether it is open, where each
 * panel stands and the chapter there, on the call's session
 * (liveCallBookings/{id}/sessions/{sessionId}.match). The coach's player,
 * the source, writes it; a peer who joins reads it before anything else and
 * opens where it says, so nobody's view changes because someone joined.
 * Contract for UI/UX: docs/specs/match-session-state.md.
 */
export interface MatchSessionState {
  open: boolean;
  panels: PanelSource[];
  /** Each panel's time when written, playing or not, and its rate. */
  states: PanelState[];
  /** The chapter panel 0 stands in, or null. */
  chapterId: string | null;
  /** When written (ISO): a playing panel has moved on since. */
  at: string;
  by: string;
}

/** The chapter a time falls in: the last that starts at or before it. */
export function chapterAt(chapters: LiveChapter[] | null | undefined, t: number): string | null {
  let found: LiveChapter | null = null;
  for (const c of chapters ?? []) if (c.startTime <= t + 0.001 && (!found || c.startTime >= found.startTime)) found = c;
  return found?.id ?? null;
}

/** Where each panel stands now: a playing one has run on since it was written, at its rate. */
export function statesNow(s: MatchSessionState, nowMs: number): PanelState[] {
  const since = Math.max(0, (nowMs - Date.parse(s.at)) / 1000);
  return s.states.map((p) => ({ ...p, time: p.playing ? p.time + since * (p.rate || 1) : p.time }));
}

/** A session's match field, if it is one. */
export function asMatchSession(v: unknown): MatchSessionState | null {
  const m = v as Partial<MatchSessionState> | null | undefined;
  if (!m || typeof m.open !== 'boolean' || !Array.isArray(m.states) || typeof m.at !== 'string') return null;
  return { open: m.open, panels: Array.isArray(m.panels) ? m.panels : [], states: m.states, chapterId: m.chapterId ?? null, at: m.at, by: m.by ?? '' };
}
